import { useState, useEffect } from "react";
import { Line, Bar } from "react-chartjs-2";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowAltCircleLeft,
  faArrowAltCircleRight,
} from "@fortawesome/free-solid-svg-icons";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { formatSalesValue } from "../../utils/formatUtils";

const CombineChart = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const { state } = useLocation();

  const { itemName, outletName } = state || {};

  const [itemList, setItemList] = useState([]);
  const [arrowCount, setArrowCount] = useState(10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [chartRate, setChartRate] = useState([]);
  const [chartUnits, setChartUnits] = useState([]);
  const [chartReceivedTime, setChartReceivedTime] = useState([]);

  const [maximum, setMaximum] = useState(0);
  const [minimum, setMinimum] = useState(0);
  const [maxUnit, setMaxUnit] = useState(0);
  const [minUnit, setMinUnit] = useState(0);
  const [totalUnit, setTotalUnit] = useState(0);
  const [slope, setSlope] = useState(0);

  // Fetch Data
  useEffect(() => {
    if (!itemName) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(
          `${baseUrl}/reqitemhistory/?item_id=${itemName}&outlet_name=${outletName}&limit=${arrowCount}`
        );

        const reversed =
          response.data.intbl_purchaserequisition_contract.reverse();

        setItemList(reversed);
      } catch (err) {
        setError("Failed to load chart data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [itemName, arrowCount, outletName, baseUrl]);

  // Process Data
  useEffect(() => {
    if (!itemList.length) return;

    const rates = itemList.map((item) => item.rate);
    const units = itemList.map((item) =>
      parseFloat(item.UnitsOrdered || 0)
    );
    const dates = itemList.map((item) =>
      new Date(item.ReceivedDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    );

    setChartRate(rates);
    setChartUnits(units);
    setChartReceivedTime(dates);

    setMaximum(Math.max(...rates));
    setMinimum(Math.min(...rates));
    setMaxUnit(Math.max(...units));
    setMinUnit(Math.min(...units));
    setTotalUnit(units.reduce((a, b) => a + b, 0));

    const first = rates[0];
    const last = rates[rates.length - 1];
    const change = (((last - first) / first) * 100).toFixed(2);
    setSlope(change);
  }, [itemList]);

  const handleMore = () =>
    setArrowCount((prev) => prev + 10);
  const handleLess = () =>
    setArrowCount((prev) => (prev - 10 < 10 ? 10 : prev - 10));

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-grow bg-gray-50 py-10 px-4">
        {/* Header */}
        <div className="bg-white max-w-6xl mx-auto rounded-2xl shadow-md border border-gray-200 p-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-800">
            {itemName}
          </h2>
          <p className="text-gray-500 mt-1">
            Outlet: <span className="font-medium">{outletName}</span>
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-10 text-gray-600 font-medium">
            Loading chart data...
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-100 text-red-600 p-4 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}

        {!loading && !error && itemList.length > 0 && (
          <>
            <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

              {/* ================= PRICE SECTION ================= */}
              <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                  Price Trend
                </h3>

                <div className="h-[300px]">
                  <Line
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        legend: { display: true },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value) => formatSalesValue(value),
                          },
                        },
                      },
                    }}
                    data={{
                      labels: chartReceivedTime,
                      datasets: [
                        {
                          label: "Rate",
                          borderColor: "#3b82f6",
                          backgroundColor: "rgba(59,130,246,0.1)",
                          tension: 0.4,
                          data: chartRate,
                        },
                      ],
                    }}
                  />
                </div>

                {/* Price Stats Table */}
                <div className="mt-6 overflow-hidden rounded-xl border border-gray-200">
                  <table className="w-full text-sm text-center">
                    <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                      <tr>
                        <th className="p-3">Highest</th>
                        <th className="p-3">Lowest</th>
                        <th className="p-3">Change %</th>
                        <th className="p-3">Duration</th>
                        <th className="p-3">Latest Rate</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white text-gray-800">
                      <tr className="border-t">
                        <td className="p-3 font-semibold">{maximum}</td>
                        <td className="p-3">{minimum}</td>
                        <td
                          className={`p-3 font-semibold ${slope > 0 ? "text-red-600" : "text-green-600"
                            }`}
                        >
                          {slope}%
                        </td>
                        <td className="p-3">
                          {chartReceivedTime.length > 1
                            ? `${Math.abs(
                              new Date(chartReceivedTime[chartReceivedTime.length - 1]) -
                              new Date(chartReceivedTime[0])
                            ) / (1000 * 3600 * 24)} days`
                            : "0 days"}

                        </td>
                        <td className="p-3 font-semibold">
                          {formatSalesValue(chartRate[chartRate.length - 1])}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>

              {/* ================= UNIT SECTION ================= */}
              <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                  Units Purchased
                </h3>

                <div className="h-[300px]">
                  <Bar
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        legend: { display: true },
                      },
                    }}
                    data={{
                      labels: chartReceivedTime,
                      datasets: [
                        {
                          label: "Units",
                          backgroundColor: "#334155",
                          borderRadius: 6,
                          data: chartUnits,
                        },
                      ],
                    }}
                  />
                </div>

                {/* Unit Stats Table */}
                <div className="mt-6 overflow-hidden rounded-xl border border-gray-200">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                      <tr>
                        <th className="px-4 py-3">Max Units</th>
                        <th className="px-4 py-3">Min Units</th>
                        <th className="px-4 py-3">Total Units</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white text-gray-800">
                      <tr className="border-t">
                        <td className="px-4 py-3 font-semibold">{maxUnit}</td>
                        <td className="px-4 py-3">{minUnit}</td>
                        <td className="px-4 py-3 font-semibold">{totalUnit}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center gap-8">
              <FontAwesomeIcon
                icon={faArrowAltCircleLeft}
                onClick={handleLess}
                className="text-3xl text-gray-600 hover:text-gray-900 cursor-pointer transition duration-200"
              />
              <FontAwesomeIcon
                icon={faArrowAltCircleRight}
                onClick={handleMore}
                className="text-3xl text-gray-600 hover:text-gray-900 cursor-pointer transition duration-200"
              />
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CombineChart;
